import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

import { AfterSalesTabsetComponent } from '../../content/after-sales/after-sales-tabset.component';
import { hasAfterSalesReadOrSuppliersAndRatesRight } from '../../services/after-sales/after-sales-functions';
import { AfterSalesStore } from '../../services/after-sales/after-sales-store.service';
import { UserService } from '../../services/user-service/user.service';

@Injectable({
  providedIn: 'root',
})
export class AfterSalesGuard {
  private readonly userService = inject(UserService);
  private readonly afterSalesStore = inject(AfterSalesStore);
  private readonly router = inject(Router);

  public async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    return this.properUserRights() && this.validUrl(route);
  }

  private properUserRights(): boolean {
    return hasAfterSalesReadOrSuppliersAndRatesRight(this.userService.getRights());
  }

  /**
   * Urls that are valid:
   * /afterSales/tabs/overview; /afterSales/tabs/details/:id; /afterSales/tabs/errorCostRates; /afterSales/tabs/supplierExceptions
   * AfterSales details for given reportId are loaded here, prior to opening details view
   * @param route current route(url)
   * @returns true if urls are valid and details are successfully loaded, otherwise false
   */
  private async validUrl(route: ActivatedRouteSnapshot): Promise<boolean> {
    const tabId: string = route.params.tabId;
    const reportId: string = route.params.id;
    const validOverviewUrl: boolean = !reportId && tabId === AfterSalesTabsetComponent.AFTERSALES_OVERVIEW_TAB;
    const validDetailsUrl: boolean = reportId && tabId === AfterSalesTabsetComponent.AFTERSALES_DETAILS_TAB;
    const validErrorRateCostUrl: boolean =
      !reportId && tabId === AfterSalesTabsetComponent.AFTERSALES_ERROR_COST_RATE_TAB;
    const validSupplierExceptionsUrl: boolean =
      !reportId && tabId === AfterSalesTabsetComponent.AFTERSALES_SUPPLIER_EXCEPTIONS_TAB;
    if (validDetailsUrl) {
      const afterSalesDetailsLoadedSuccess: boolean = await this.afterSalesStore.getAfterSalesDetails(reportId);
      if (!afterSalesDetailsLoadedSuccess) {
        this.redirectToDefaultLocation();
        return false;
      }
    }
    const validAfterSalesUrl: boolean =
      validOverviewUrl || validDetailsUrl || validErrorRateCostUrl || validSupplierExceptionsUrl;
    if (!validAfterSalesUrl) {
      this.redirectToDefaultLocation();
    }
    return validAfterSalesUrl;
  }

  private redirectToDefaultLocation(): void {
    this.router.navigate(['/recourseBooking']);
  }

  private redirectToTab(tabId: string): void {
    this.router.navigate([`/afterSales/tabs/${tabId}`]);
  }
}

export const afterSalesGuardCanActivate: CanActivateFn = async (route: ActivatedRouteSnapshot): Promise<boolean> =>
  inject(AfterSalesGuard).canActivate(route);
